/**
 * Comprehensive Negotiation Dashboard
 * Provides overview of contract negotiation status and actions
 */

import React from 'react';
// import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useGetAgreeableClausesQuery } from '@/apis/extended/agreeableClauseApi';
import { useGetContractQuery } from '@/apis/extended/contractApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useClauseNotifications } from '@/hooks/useClauseNotifications';
import { useClauseState } from '@/hooks/useClauseState';
import { useContractRatification } from '@/hooks/useContractRatification';
import { ClauseProperties } from '@/utils/clauseIdUtils';
import { BackendUnavailableMessage } from './BackendUnavailableMessage';

interface NegotiationDashboardProps {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
  /** When false, hides the summary card (e.g. parent already renders NegotiationStatusBar). */
  showContractStatusCard?: boolean;
  /**
   * When false, omits the bottom "Contract Actions" card (dashboard + view
   * contract). Caller should render those actions (e.g. deal-room header).
   * Ratify + agreeable-clauses notice still render below the dashboard body.
   */
  showContractNavActionsCard?: boolean;
  onClauseClick?: (clause: ClauseProperties) => void;
  onRatifyContract?: () => void;
  onCreateAddendum?: (clauseId: string) => void;
  onRequestExtension?: (clauseId: string) => void;
  onCreateRepairRequest?: (clauseId: string) => void;
}

export const NegotiationDashboard: React.FC<NegotiationDashboardProps> = ({
  contractId,
  userRole,
  showContractStatusCard = true,
  showContractNavActionsCard = true,
  onClauseClick,
  onRatifyContract,
  onCreateAddendum,
  onRequestExtension,
  onCreateRepairRequest,
}) => {
  const navigate = useNavigate();

  const {
    clauses,
    urgentClauses,
    attentionClauses,
    negotiationSummary,
    hasPendingNegotiations,
    isLoadingClauses,
    isErrorClauses,
    isBackendUnavailable,
    refetchClauses,
  } = useClauseState({
    contractId,
    userRole,
    contractRatified: false, // Will be updated by ratification hook
  });

  const {
    isRatified,
    ratificationDate,
    specialCircumstances,
    ratifyContract,
    getAvailableActions,
  } = useContractRatification(contractId, userRole);

  const { showClauseActionNotification, showContractStatusNotification } =
    useClauseNotifications(contractId, userRole, isRatified);

  // Get contract data for navigation
  const { data: contract } = useGetContractQuery(contractId, {
    skip: !contractId,
  });

  // Get agreeable clauses to check if all are agreed
  const { data: agreeableClausesData = [] } = useGetAgreeableClausesQuery(
    contractId,
    { skip: !contractId }
  );

  // Get property dashboard route based on user role
  const getPropertyDashboardRoute = () => {
    switch (userRole) {
      case 'buyer':
        return `/buyer/property-dashboard/${contractId}`;
      case 'seller':
        return `/seller/property-dashboard/${contractId}`;
      case 'agent':
        return `/agent/property-dashboard/${contractId}`;
      default:
        return `/property-dashboard/${contractId}`;
    }
  };

  // Check if all clauses are agreeable (for ratification eligibility)
  const totalClausesCount = negotiationSummary?.totalClauses || 0;
  const agreeableClausesCount = agreeableClausesData.length;
  const allClausesAgreeable =
    totalClausesCount > 0 && agreeableClausesCount === totalClausesCount;

  // Calculate negotiation progress
  const calculateProgress = () => {
    if (!negotiationSummary) return 0;
    const { totalClauses, acceptedClauses, ratifiedClauses } =
      negotiationSummary;
    if (totalClauses === 0) return 0;
    return Math.round(
      ((acceptedClauses + ratifiedClauses) / totalClauses) * 100
    );
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const colors = {
      original: 'bg-blue-100 text-blue-800',
      proposed: 'bg-yellow-100 text-yellow-800',
      countered: 'bg-orange-100 text-orange-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      ratified: 'bg-purple-100 text-purple-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // Handle clause actions
  const handleClauseAction = async (
    clause: ClauseProperties,
    action: string
  ) => {
    try {
      switch (action) {
        case 'addendum':
          onCreateAddendum?.(clause.clauseId);
          break;
        case 'extension':
          onRequestExtension?.(clause.clauseId);
          break;
        case 'repair_request':
          onCreateRepairRequest?.(clause.clauseId);
          break;
        default:
          onClauseClick?.(clause);
      }
    } catch (error) {
      console.error('Error handling clause action:', error);
    }
  };

  if (isLoadingClauses) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Loading Negotiation Dashboard
            </h3>
            <p className="text-gray-500">
              Please wait while we load the contract details...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isErrorClauses) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="text-center py-8">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Dashboard
            </h3>
            <p className="text-gray-500">
              There was an error loading the negotiation data.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show backend unavailable message
  if (isBackendUnavailable) {
    return (
      <div className="space-y-6">
        <BackendUnavailableMessage
          onRetry={() => {
            // Retry all queries
            refetchClauses();
          }}
          contractId={contractId}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showContractStatusCard && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Contract Negotiation Status
              </CardTitle>
              <Badge
                className={
                  isRatified
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-blue-100 text-blue-800'
                }
              >
                {isRatified ? 'Ratified' : 'Active'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {negotiationSummary?.totalClauses || 0}
                </div>
                <div className="text-sm text-gray-500">Total Clauses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {negotiationSummary?.acceptedClauses || 0}
                </div>
                <div className="text-sm text-gray-500">Accepted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {negotiationSummary?.pendingClauses || 0}
                </div>
                <div className="text-sm text-gray-500">Pending</div>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Negotiation Progress
                </span>
                <span className="text-sm text-gray-500">
                  {calculateProgress()}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Urgent Clauses */}
      {urgentClauses.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Urgent Clauses ({urgentClauses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {urgentClauses.map((clause) => (
                <div
                  key={clause.hierarchicalId}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {clause.clauseId}
                    </h4>
                    <div className="text-sm text-gray-600">
                      Status:{' '}
                      <Badge className={getStatusColor(clause.status)}>
                        {clause.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getAvailableActions(clause).map((action) => (
                      <Button
                        key={action}
                        size="sm"
                        variant="outline"
                        onClick={() => handleClauseAction(clause, action)}
                      >
                        {action.replace('_', ' ').toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attention Clauses */}
      {attentionClauses.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Clauses Requiring Attention ({attentionClauses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {attentionClauses.map((clause) => (
                <div
                  key={clause.hierarchicalId}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-200"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {clause.clauseId}
                    </h4>
                    <div className="text-sm text-gray-600">
                      Status:{' '}
                      <Badge className={getStatusColor(clause.status)}>
                        {clause.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getAvailableActions(clause).map((action) => (
                      <Button
                        key={action}
                        size="sm"
                        variant="outline"
                        onClick={() => handleClauseAction(clause, action)}
                      >
                        {action.replace('_', ' ').toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Special Circumstances (for ratified contracts) */}
      {isRatified && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Special Circumstances
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {specialCircumstances.addendums.length}
                </div>
                <div className="text-sm text-gray-500">Addendums</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {specialCircumstances.extensions.length}
                </div>
                <div className="text-sm text-gray-500">Extensions</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {specialCircumstances.repairRequests.length}
                </div>
                <div className="text-sm text-gray-500">Repair Requests</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract Actions — full card; omit when parent renders nav (e.g. deal room header) */}
      {showContractNavActionsCard && (
        <Card>
          <CardHeader>
            <CardTitle>Contract Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {/* Only show Ratify button if not ratified AND all clauses are agreeable */}
              {!isRatified && allClausesAgreeable && totalClausesCount > 0 && (
                <Button
                  onClick={onRatifyContract}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Ratify Contract
                </Button>
              )}

              {/* Property Dashboard Button */}
              <Button
                variant="outline"
                onClick={() => navigate(getPropertyDashboardRoute())}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                {userRole === 'buyer'
                  ? 'Purchase Dashboard'
                  : userRole === 'seller'
                  ? 'Sale Dashboard'
                  : 'Property Dashboard'}
              </Button>

              {/* View Full Contract Button */}
              <Button
                variant="outline"
                onClick={() => navigate(`/view-contract/${contractId}`)}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Full Contract
              </Button>
            </div>

            {/* Show message if not all clauses are agreeable yet */}
            {!isRatified && !allClausesAgreeable && totalClausesCount > 0 && (
              <p className="text-sm text-gray-600 mt-3">
                <AlertTriangle className="h-4 w-4 inline mr-1 text-orange-500" />
                All clauses must be marked as agreeable before ratifying the
                contract. ({agreeableClausesCount} of {totalClausesCount}{' '}
                clauses agreed)
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {!showContractNavActionsCard && !isRatified && totalClausesCount > 0 && (
        <div className="mt-2 space-y-3">
          {allClausesAgreeable && (
            <Button
              onClick={onRatifyContract}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Ratify Contract
            </Button>
          )}
          {!allClausesAgreeable && (
            <p className="text-sm text-gray-600">
              <AlertTriangle className="h-4 w-4 inline mr-1 text-orange-500" />
              All clauses must be marked as agreeable before ratifying the
              contract. ({agreeableClausesCount} of {totalClausesCount} clauses
              agreed)
            </p>
          )}
        </div>
      )}
    </div>
  );
};
